import { ConnectionConfigSchema } from "@aa-sdk/core";
import { TurnkeyClient } from "@turnkey/http";
import EventEmitter from "eventemitter3";
import { jwtDecode } from "jwt-decode";
import { sha256 } from "viem";
import { NotAuthenticatedError, OAuthProvidersError } from "../errors.js";
import { getDefaultProviderCustomization } from "../oauth.js";
import { base64UrlEncode } from "../utils/base64UrlEncode.js";
import { resolveRelativeUrl } from "../utils/resolveRelativeUrl.js";
import { assertNever } from "../utils/typeAssertions.js";
import { VERSION } from "../version.js";
const MFA_PAYLOAD = {
    GET: "get_mfa",
    ADD: "add_mfa",
    DELETE: "delete_mfas",
    VERIFY: "verify_mfa",
    LIST: "list_mfas",
};
/**
 * Base class for all Alchemy Signer clients
 */
export class BaseSignerClient {
    /**
     * Create a new instance of the Alchemy Signer client
     *
     * @param {BaseSignerClientParams} params the parameters required to create the client
     */
    constructor(params) {
        Object.defineProperty(this, "_user", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "connectionConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "turnkeyClient", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rootOrg", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "eventEmitter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "oauthConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Asynchronously fetches and sets the OAuth configuration.
         *
         * @returns {Promise<OauthConfig>} A promise that resolves to the OAuth configuration
         */
        Object.defineProperty(this, "initOauth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                this.oauthConfig = await this.getOauthConfig();
                return this.oauthConfig;
            }
        });
        // #endregion
        // #region PUBLIC METHODS
        /**
         * Listen to events emitted by the client
         *
         * @param {AlchemySignerClientEvent} event the event you want to listen to
         * @param {AlchemySignerClientEvents[AlchemySignerClientEvent]} listener the callback function to execute when an event is fired
         * @returns {() => void} a function that will remove the listener when called
         */
        Object.defineProperty(this, "on", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event, listener) => {
                this.eventEmitter.on(event, listener);
                return () => this.eventEmitter.removeListener(event, listener);
            }
        });
        /**
         * Handles the creation of authenticators using WebAuthn attestation and the provided options. Requires the user to be authenticated.
         *
         * @param {CredentialCreationOptions} options The options used to create the WebAuthn attestation
         * @returns {Promise<string[]>} A promise that resolves to an array of authenticator IDs
         * @throws {NotAuthenticatedError} If the user is not authenticated
         */
        Object.defineProperty(this, "addPasskey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (options) => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                const { attestation, challenge } = await this.getWebAuthnAttestation(options);
                const { activity } = await this.turnkeyClient.createAuthenticators({
                    type: "ACTIVITY_TYPE_CREATE_AUTHENTICATORS_V2",
                    timestampMs: Date.now().toString(),
                    organizationId: this.user.orgId,
                    parameters: {
                        userId: this.user.userId,
                        authenticators: [
                            {
                                attestation,
                                authenticatorName: `passkey-${Date.now().toString()}`,
                                challenge: base64UrlEncode(challenge),
                            },
                        ],
                    },
                });
                const { authenticatorIds } = await this.pollActivityCompletion(activity, this.user.orgId, "createAuthenticatorsResult");
                return authenticatorIds;
            }
        });
        /**
         * Retrieves the status of the passkey for the current user. Requires the user to be authenticated.
         *
         * @returns {Promise<{ isPasskeyAdded: boolean }>} A promise that resolves to an object containing the passkey status
         * @throws {NotAuthenticatedError} If the user is not authenticated
         */
        Object.defineProperty(this, "getPasskeyStatus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                const resp = await this.turnkeyClient.getAuthenticators({
                    organizationId: this.user.orgId,
                    userId: this.user.userId,
                });
                return {
                    isPasskeyAdded: resp.authenticators.some((it) => it.authenticatorName.startsWith("passkey-")),
                };
            }
        });
        /**
         * Adds an OAuth provider for the authenticated user using the provided parameters. Throws an error if the user is not authenticated.
         *
         * @param {AddOauthProviderParams} params The parameters for adding an OAuth provider, including `providerName` and `oidcToken`.
         * @throws {NotAuthenticatedError} Throws if the user is not authenticated.
         * @returns {Promise<void>} A Promise that resolves when the OAuth provider is added.
         */
        Object.defineProperty(this, "addOauthProvider", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                const { providerName, oidcToken } = params;
                const stampedRequest = await this.turnkeyClient.stampCreateOauthProviders({
                    type: "ACTIVITY_TYPE_CREATE_OAUTH_PROVIDERS",
                    timestampMs: Date.now().toString(),
                    organizationId: this.user.orgId,
                    parameters: {
                        userId: this.user.userId,
                        oauthProviders: [{ providerName, oidcToken }],
                    },
                });
                await this.request("/v1/add-oauth-provider", { stampedRequest });
            }
        });
        /**
         * Retrieves the current user or fetches the user information if not already available.
         *
         * @param {string} [orgId] optional organization ID, defaults to the user's organization ID
         * @param {string} idToken an OIDC ID token containing additional user information
         * @returns {Promise<User>} A promise that resolves to the user object
         * @throws {Error} if no organization ID is provided when there is no current user
         */
        Object.defineProperty(this, "whoami", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (orgId = this.user?.orgId, idToken) => {
                if (this.user) {
                    return this.user;
                }
                if (!orgId) {
                    throw new Error("No orgId provided");
                }
                const stampedRequest = await this.turnkeyClient.stampGetWhoami({
                    organizationId: orgId,
                });
                const user = await this.request("/v1/whoami", {
                    stampedRequest,
                });
                if (idToken) {
                    const claims = jwtDecode(idToken);
                    user.idToken = idToken;
                    user.claims = claims;
                    if (typeof claims.email === "string") {
                        user.email = claims.email;
                    }
                }
                const credentialId = (() => {
                    try {
                        return JSON.parse(stampedRequest?.stamp.stampHeaderValue)
                            .credentialId;
                    }
                    catch (e) {
                        return undefined;
                    }
                })();
                this.user = {
                    ...user,
                    credentialId,
                };
                return this.user;
            }
        });
        /**
         * Generates a stamped whoami request for the current user. This request can then be used to call /signer/v1/whoami to get the user information.
         * This is useful if you want to get the user information in a different context like a server. You can pass the stamped request to the server
         * and then call our API to get the user information. Using this stamp is the most trusted way to get the user information since a stamp can only
         * belong to the user who created it.
         *
         * @returns {Promise<TSignedRequest>} a promise that resolves to the "whoami" information for the logged in user
         * @throws {Error} if no organization ID is provided
         */
        Object.defineProperty(this, "stampWhoami", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                if (!this.user) {
                    throw new Error("User must be authenticated to stamp a whoami request");
                }
                return await this.turnkeyClient.stampGetWhoami({
                    organizationId: this.user.orgId,
                });
            }
        });
        /**
         * Generates a stamped getOrganization request for the current user.
         *
         * @returns {Promise<TSignedRequest>} a promise that resolves to the "getOrganization" information for the logged in user
         * @throws {Error} if no user is authenticated
         */
        Object.defineProperty(this, "stampGetOrganization", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                if (!this.user) {
                    throw new Error("User must be authenticated to stamp a get organization request");
                }
                return await this.turnkeyClient.stampGetOrganization({
                    organizationId: this.user.orgId,
                });
            }
        });
        /**
         * Creates an API key that can take any action on behalf of the current user.
         * (Note that this method is currently experimental and is subject to change.)
         *
         * @param {CreateApiKeyParams} params Parameters for creating the API key.
         * @param {string} params.name Name of the API key.
         * @param {string} params.publicKey Public key to be used for the API key.
         * @param {number} params.expirationSec Number of seconds until the API key expires.
         * @throws {Error} If there is no authenticated user or the API key creation fails.
         */
        Object.defineProperty(this, "experimental_createApiKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                if (!this.user) {
                    throw new Error("User must be authenticated to create api key");
                }
                const resp = await this.turnkeyClient.createApiKeys({
                    type: "ACTIVITY_TYPE_CREATE_API_KEYS_V2",
                    timestampMs: new Date().getTime().toString(),
                    organizationId: this.user.orgId,
                    parameters: {
                        apiKeys: [
                            {
                                apiKeyName: params.name,
                                publicKey: params.publicKey,
                                curveType: "API_KEY_CURVE_P256",
                                expirationSeconds: params.expirationSec.toString(),
                            },
                        ],
                        userId: this.user.userId,
                    },
                });
                if (resp.activity.status !== "ACTIVITY_STATUS_COMPLETED") {
                    throw new Error("Failed to create api key");
                }
            }
        });
        /**
         * Looks up information based on an email address.
         *
         * @param {string} email the email address to look up
         * @returns {Promise<any>} the result of the lookup request
         */
        Object.defineProperty(this, "lookupUserByEmail", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (email) => {
                return this.request("/v1/lookup", { email });
            }
        });
        /**
         * This will sign a message with the user's private key, without doing any transformations on the message.
         * For SignMessage or SignTypedData, the caller should hash the message before calling this method and pass
         * that result here.
         *
         * @param {Hex} msg the hex representation of the bytes to sign
         * @param {string} mode specify if signing should happen for solana or ethereum
         * @returns {Promise<Hex>} the signature over the raw hex
         */
        Object.defineProperty(this, "signRawMessage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (msg, mode = "ETHEREUM") => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                if (!this.user.solanaAddress && mode === "SOLANA") {
                    // TODO: we need to add backwards compatibility for users who signed up before we added Solana support
                    throw new Error("No Solana address available for the user");
                }
                const stampedRequest = await this.turnkeyClient.stampSignRawPayload({
                    organizationId: this.user.orgId,
                    type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOAD_V2",
                    timestampMs: Date.now().toString(),
                    parameters: {
                        encoding: "PAYLOAD_ENCODING_HEXADECIMAL",
                        hashFunction: mode === "ETHEREUM"
                            ? "HASH_FUNCTION_NO_OP"
                            : "HASH_FUNCTION_NOT_APPLICABLE",
                        payload: msg,
                        signWith: mode === "ETHEREUM" ? this.user.address : this.user.solanaAddress,
                    },
                });
                const { signature } = await this.request("/v1/sign-payload", {
                    stampedRequest,
                });
                return signature;
            }
        });
        /**
         * Returns the current user or null if no user is set.
         *
         * @returns {User | null} the current user object or null if no user is available
         */
        Object.defineProperty(this, "getUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.user ?? null;
            }
        });
        /**
         * Sends a POST request to the given signer route with the specified body and returns the response.
         * Not intended to be used directly, use the specific methods instead on the client instead.
         *
         * @param {SignerRoutes} route The route to which the request should be sent
         * @param {SignerBody<R>} body The request body containing the data to be sent
         * @returns {Promise<SignerResponse<R>>} A promise that resolves to the response from the signer
         */
        Object.defineProperty(this, "request", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (route, body) => {
                const url = this.connectionConfig.rpcUrl ?? "https://api.g.alchemy.com";
                const basePath = "/signer";
                const headers = new Headers();
                headers.append("Alchemy-AA-Sdk-Version", VERSION);
                headers.append("Content-Type", "application/json");
                if (this.connectionConfig.apiKey) {
                    headers.append("Authorization", `Bearer ${this.connectionConfig.apiKey}`);
                }
                else if (this.connectionConfig.jwt) {
                    headers.append("Authorization", `Bearer ${this.connectionConfig.jwt}`);
                }
                const response = await fetch(`${url}${basePath}${route}`, {
                    method: "POST",
                    body: JSON.stringify(body),
                    headers,
                });
                if (!response.ok) {
                    throw new Error(await response.text());
                }
                const json = await response.json();
                return json;
            }
        });
        /**
         * Retrieves the list of MFA factors configured for the current user.
         *
         * @returns {Promise<{ multiFactors: MfaFactor[] }>} A promise that resolves to an array of configured MFA factors
         * @throws {NotAuthenticatedError} If no user is authenticated
         */
        Object.defineProperty(this, "getMfaFactors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                const stampedRequest = await this.turnkeyClient.stampSignRawPayload({
                    organizationId: this.user.orgId,
                    type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOAD_V2",
                    timestampMs: Date.now().toString(),
                    parameters: {
                        encoding: "PAYLOAD_ENCODING_HEXADECIMAL",
                        hashFunction: "HASH_FUNCTION_NO_OP",
                        payload: MFA_PAYLOAD.LIST,
                        signWith: this.user.address,
                    },
                });
                return this.request("/v1/auth-list-multi-factors", {
                    stampedRequest,
                });
            }
        });
        /**
         * Initiates the setup of a new MFA factor for the current user. Mfa will need to be verified before it is active.
         *
         * @param {AddMfaParams} params The parameters required to enable a new MFA factor
         * @returns {Promise<AddMfaResult>} A promise that resolves to the factor setup information
         * @throws {NotAuthenticatedError} If no user is authenticated
         * @throws {Error} If an unsupported factor type is provided
         */
        Object.defineProperty(this, "addMfa", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                const stampedRequest = await this.turnkeyClient.stampSignRawPayload({
                    organizationId: this.user.orgId,
                    type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOAD_V2",
                    timestampMs: Date.now().toString(),
                    parameters: {
                        encoding: "PAYLOAD_ENCODING_HEXADECIMAL",
                        hashFunction: "HASH_FUNCTION_NO_OP",
                        payload: MFA_PAYLOAD.ADD,
                        signWith: this.user.address,
                    },
                });
                switch (params.multiFactorType) {
                    case "totp":
                        return this.request("/v1/auth-request-multi-factor", {
                            stampedRequest,
                            multiFactorType: params.multiFactorType,
                        });
                    default:
                        throw new Error(`Unsupported MFA factor type: ${params.multiFactorType}`);
                }
            }
        });
        /**
         * Verifies a newly created MFA factor to complete the setup process.
         *
         * @param {VerifyMfaParams} params The parameters required to verify the MFA factor
         * @returns {Promise<{ multiFactors: MfaFactor[] }>} A promise that resolves to the updated list of MFA factors
         * @throws {NotAuthenticatedError} If no user is authenticated
         */
        Object.defineProperty(this, "verifyMfa", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                const stampedRequest = await this.turnkeyClient.stampSignRawPayload({
                    organizationId: this.user.orgId,
                    type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOAD_V2",
                    timestampMs: Date.now().toString(),
                    parameters: {
                        encoding: "PAYLOAD_ENCODING_HEXADECIMAL",
                        hashFunction: "HASH_FUNCTION_NO_OP",
                        payload: MFA_PAYLOAD.VERIFY,
                        signWith: this.user.address,
                    },
                });
                return this.request("/v1/auth-verify-multi-factor", {
                    stampedRequest,
                    multiFactorId: params.multiFactorId,
                    multiFactorCode: params.multiFactorCode,
                });
            }
        });
        /**
         * Removes existing MFA factors by ID.
         *
         * @param {RemoveMfaParams} params The parameters specifying which factors to disable
         * @returns {Promise<{ multiFactors: MfaFactor[] }>} A promise that resolves to the updated list of MFA factors
         * @throws {NotAuthenticatedError} If no user is authenticated
         */
        Object.defineProperty(this, "removeMfa", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                const stampedRequest = await this.turnkeyClient.stampSignRawPayload({
                    organizationId: this.user.orgId,
                    type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOAD_V2",
                    timestampMs: Date.now().toString(),
                    parameters: {
                        encoding: "PAYLOAD_ENCODING_HEXADECIMAL",
                        hashFunction: "HASH_FUNCTION_NO_OP",
                        payload: MFA_PAYLOAD.DELETE,
                        signWith: this.user.address,
                    },
                });
                return this.request("/v1/auth-delete-multi-factors", {
                    stampedRequest,
                    multiFactorIds: params.multiFactorIds,
                });
            }
        });
        /**
         * Validates multiple MFA factors using the provided encrypted payload and MFA codes.
         *
         * @param {ValidateMultiFactorsParams} params The validation parameters
         * @returns {Promise<{ bundle: string }>} A promise that resolves to an object containing the credential bundle
         * @throws {Error} If no credential bundle is returned from the server
         */
        Object.defineProperty(this, "validateMultiFactors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                // Send the encryptedPayload plus TOTP codes, etc:
                const response = await this.request("/v1/auth-validate-multi-factors", {
                    encryptedPayload: params.encryptedPayload,
                    multiFactors: params.multiFactors,
                });
                // The server is expected to return the *decrypted* payload in `response.payload.credentialBundle`
                if (!response.payload || !response.payload.credentialBundle) {
                    throw new Error("Request to validateMultiFactors did not return a credential bundle");
                }
                return {
                    bundle: response.payload.credentialBundle,
                };
            }
        });
        // #endregion
        // #region PRIVATE METHODS
        Object.defineProperty(this, "exportAsSeedPhrase", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (stamper) => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                const { wallets } = await this.turnkeyClient.getWallets({
                    organizationId: this.user.orgId,
                });
                const walletAccounts = await Promise.all(wallets.map(({ walletId }) => this.turnkeyClient.getWalletAccounts({
                    organizationId: this.user.orgId,
                    walletId,
                }))).then((x) => x.flatMap((x) => x.accounts));
                const walletAccount = walletAccounts.find((x) => x.address === this.user.address);
                if (!walletAccount) {
                    throw new Error(`Could not find wallet associated with ${this.user.address}`);
                }
                const { activity } = await this.turnkeyClient.exportWallet({
                    organizationId: this.user.orgId,
                    type: "ACTIVITY_TYPE_EXPORT_WALLET",
                    timestampMs: Date.now().toString(),
                    parameters: {
                        walletId: walletAccount.walletId,
                        targetPublicKey: stamper.publicKey(),
                    },
                });
                const { exportBundle } = await this.pollActivityCompletion(activity, this.user.orgId, "exportWalletResult");
                const result = await stamper.injectWalletExportBundle(exportBundle);
                if (!result) {
                    throw new Error("Failed to inject wallet export bundle");
                }
                return result;
            }
        });
        Object.defineProperty(this, "exportAsPrivateKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (stamper) => {
                if (!this.user) {
                    throw new NotAuthenticatedError();
                }
                const { activity } = await this.turnkeyClient.exportWalletAccount({
                    organizationId: this.user.orgId,
                    type: "ACTIVITY_TYPE_EXPORT_WALLET_ACCOUNT",
                    timestampMs: Date.now().toString(),
                    parameters: {
                        address: this.user.address,
                        targetPublicKey: stamper.publicKey(),
                    },
                });
                const { exportBundle } = await this.pollActivityCompletion(activity, this.user.orgId, "exportWalletAccountResult");
                const result = await stamper.injectKeyExportBundle(exportBundle);
                if (!result) {
                    throw new Error("Failed to inject wallet export bundle");
                }
                return result;
            }
        });
        /**
         * Returns the authentication url for the selected OAuth Proivder
         *
         * @example
         * ```ts
         *
         * cosnt oauthParams = {
         *  authProviderId: "google",
         *  isCustomProvider: false,
         *  auth0Connection: undefined,
         *  scope: undefined,
         *  claims: undefined,
         *  mode: "redirect",
         *  redirectUrl: "https://your-url-path/oauth-return",
         *  expirationSeconds: 3000
         * };
         *
         * const turnkeyPublicKey = await this.initIframeStamper();
         * const oauthCallbackUrl = this.oauthCallbackUrl;
         * const oauthConfig = this.getOauthConfig() // Optional value for OauthConfig()
         * const usesRelativeUrl = true // Optional value to determine if we use a relative (or absolute) url for the `redirect_url`
         *
         * const oauthProviderUrl = getOauthProviderUrl({
         *  oauthParams,
         *  turnkeyPublicKey,
         *  oauthCallbackUrl
         * })
         *
         * ```
         * @param {GetOauthProviderUrlArgs} args Required. The Oauth provider's auth parameters
         *
         * @returns {Promise<string>} returns the Oauth provider's url
         */
        Object.defineProperty(this, "getOauthProviderUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (args) => {
                const { oauthParams, turnkeyPublicKey, oauthCallbackUrl, oauthConfig, usesRelativeUrl = true, } = args;
                const { authProviderId, isCustomProvider, auth0Connection, scope: providedScope, claims: providedClaims, otherParameters: providedOtherParameters, mode, redirectUrl, expirationSeconds, } = oauthParams;
                const { codeChallenge, requestKey, authProviders } = oauthConfig ?? (await this.getOauthConfigForMode(mode));
                if (!authProviders) {
                    throw new OAuthProvidersError();
                }
                const authProvider = authProviders.find((provider) => provider.id === authProviderId &&
                    !!provider.isCustomProvider === !!isCustomProvider);
                if (!authProvider) {
                    throw new Error(`No auth provider found with id ${authProviderId}`);
                }
                let scope = providedScope;
                let claims = providedClaims;
                let otherParameters = providedOtherParameters;
                if (!isCustomProvider) {
                    const defaultCustomization = getDefaultProviderCustomization(authProviderId);
                    scope ?? (scope = defaultCustomization?.scope);
                    claims ?? (claims = defaultCustomization?.claims);
                    otherParameters ?? (otherParameters = defaultCustomization?.otherParameters);
                }
                if (!scope) {
                    throw new Error(`Default scope not known for provider ${authProviderId}`);
                }
                const { authEndpoint, clientId } = authProvider;
                const nonce = this.getOauthNonce(turnkeyPublicKey);
                const stateObject = {
                    authProviderId,
                    isCustomProvider,
                    requestKey,
                    turnkeyPublicKey,
                    expirationSeconds,
                    redirectUrl: mode === "redirect"
                        ? usesRelativeUrl
                            ? resolveRelativeUrl(redirectUrl)
                            : redirectUrl
                        : undefined,
                    openerOrigin: mode === "popup" ? window.location.origin : undefined,
                };
                const state = base64UrlEncode(new TextEncoder().encode(JSON.stringify(stateObject)));
                const authUrl = new URL(authEndpoint);
                const params = {
                    redirect_uri: oauthCallbackUrl,
                    response_type: "code",
                    scope,
                    state,
                    code_challenge: codeChallenge,
                    code_challenge_method: "S256",
                    prompt: "select_account",
                    client_id: clientId,
                    nonce,
                    ...otherParameters,
                };
                if (claims) {
                    params.claims = claims;
                }
                if (auth0Connection) {
                    params.connection = auth0Connection;
                }
                Object.keys(params).forEach((param) => {
                    params[param] && authUrl.searchParams.append(param, params[param]);
                });
                const [urlPath, searchParams] = authUrl.href.split("?");
                return `${urlPath?.replace(/\/$/, "")}?${searchParams}`;
            }
        });
        Object.defineProperty(this, "getOauthConfigForMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (mode) => {
                if (this.oauthConfig) {
                    return this.oauthConfig;
                }
                else if (mode === "redirect") {
                    return this.initOauth();
                }
                else {
                    throw new Error("enablePopupOauth must be set in configuration or signer.preparePopupOauth must be called before using popup-based OAuth login");
                }
            }
        });
        // eslint-disable-next-line eslint-rules/require-jsdoc-on-reexported-functions
        Object.defineProperty(this, "pollActivityCompletion", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (activity, organizationId, resultKey) => {
                if (activity.status === "ACTIVITY_STATUS_COMPLETED") {
                    return activity.result[resultKey];
                }
                const { activity: { status, id, result }, } = await this.turnkeyClient.getActivity({
                    activityId: activity.id,
                    organizationId,
                });
                if (status === "ACTIVITY_STATUS_COMPLETED") {
                    return result[resultKey];
                }
                if (status === "ACTIVITY_STATUS_FAILED" ||
                    status === "ACTIVITY_STATUS_REJECTED" ||
                    status === "ACTIVITY_STATUS_CONSENSUS_NEEDED") {
                    throw new Error(`Failed to get activity with with id ${id} (status: ${status})`);
                }
                // TODO: add ability to configure this + add exponential backoff
                await new Promise((resolve) => setTimeout(resolve, 500));
                return this.pollActivityCompletion(activity, organizationId, resultKey);
            }
        });
        // #endregion
        /**
         * Turnkey requires the nonce in the id token to be in this format.
         *
         * @param {string} turnkeyPublicKey key from a Turnkey iframe
         * @returns {string} nonce to be used in OIDC
         */
        Object.defineProperty(this, "getOauthNonce", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (turnkeyPublicKey) => {
                return sha256(new TextEncoder().encode(turnkeyPublicKey)).slice(2);
            }
        });
        const { stamper, connection, rootOrgId } = params;
        this.rootOrg = rootOrgId ?? "24c1acf5-810f-41e0-a503-d5d13fa8e830";
        this.eventEmitter = new EventEmitter();
        this.connectionConfig = ConnectionConfigSchema.parse(connection);
        this.turnkeyClient = new TurnkeyClient({ baseUrl: "https://api.turnkey.com" }, stamper);
    }
    get user() {
        return this._user;
    }
    set user(user) {
        const previousUser = this._user;
        this._user = user;
        if (user && !previousUser) {
            this.eventEmitter.emit("connected", user);
        }
        else if (!user && previousUser) {
            this.eventEmitter.emit("disconnected");
        }
    }
    /**
     * Sets the stamper of the TurnkeyClient.
     *
     * @param {TurnkeyClient["stamper"]} stamper the stamper function to set for the TurnkeyClient
     */
    setStamper(stamper) {
        this.turnkeyClient.stamper = stamper;
    }
    /**
     * Exports wallet credentials based on the specified type, either as a SEED_PHRASE or PRIVATE_KEY.
     *
     * @param {object} params The parameters for exporting the wallet
     * @param {ExportWalletStamper} params.exportStamper The stamper used for exporting the wallet
     * @param {"SEED_PHRASE" | "PRIVATE_KEY"} params.exportAs Specifies the format for exporting the wallet, either as a SEED_PHRASE or PRIVATE_KEY
     * @returns {Promise<boolean>} A promise that resolves to true if the export is successful
     */
    exportWalletInner(params) {
        const { exportAs } = params;
        switch (exportAs) {
            case "PRIVATE_KEY":
                return this.exportAsPrivateKey(params.exportStamper);
            case "SEED_PHRASE":
                return this.exportAsSeedPhrase(params.exportStamper);
            default:
                assertNever(exportAs, `Unknown export mode: ${exportAs}`);
        }
    }
}
//# sourceMappingURL=base.js.map